<template>
    <div class="container_view">
        <v-card class="content_card" elevation="10" variant="flat">
            <div>
                <header class="d-flex align-center justify-space-between">
                    <v-card-title> Detalle de carga </v-card-title>
                    <v-btn
                        variant="text"
                        color="indigo-darken-3"
                        prepend-icon="mdi-arrow-left"
                        text="Volver"
                        class="mr-3"
                        @click="$router.push('/SGCCV/database/imports')"
                    ></v-btn>
                </header>
                <v-divider class="mx-3"></v-divider>
            </div>

            <div class="content_main">
                <v-progress-linear
                    v-if="importData"
                    :model-value="importData.progress"
                    color="indigo-darken-3"
                    height="8"
                    class="mb-4"
                ></v-progress-linear>

                <v-row v-if="importData" dense>
                    <v-col cols="12" md="6">
                        <v-list density="compact">
                            <v-list-item>
                                <v-list-item-title>Archivo</v-list-item-title>
                                <v-list-item-subtitle>{{ importData.originalFilename }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Tipo</v-list-item-title>
                                <v-list-item-subtitle>{{ importData.importType }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Estado</v-list-item-title>
                                <v-list-item-subtitle>
                                    <v-chip :color="statusInfo(importData.status).color" size="small">
                                        {{ statusInfo(importData.status).label }}
                                    </v-chip>
                                </v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item v-if="importData.failureMessage">
                                <v-list-item-title>Error</v-list-item-title>
                                <v-list-item-subtitle class="text-red">{{ importData.failureMessage }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-list density="compact">
                            <v-list-item>
                                <v-list-item-title>Procesadas / Total</v-list-item-title>
                                <v-list-item-subtitle>{{ importData.processedRows }} / {{ importData.totalRows }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Éxitosas</v-list-item-title>
                                <v-list-item-subtitle>{{ importData.successfulRows }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Fallidas</v-list-item-title>
                                <v-list-item-subtitle>{{ importData.failedRows }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Iniciado / Terminado</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ formatDate(importData.startedAt) }} / {{ formatDate(importData.finishedAt) }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </v-col>
                </v-row>

                <div class="logs_header">
                    <span class="text-subtitle-1">Registro de eventos</span>
                </div>

                <div class="logs_container">
                    <v-table v-if="logs.length" density="compact">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Nivel</th>
                                <th>Evento</th>
                                <th>Mensaje</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="log in logs" :key="log.id">
                                <td class="text-caption">{{ formatDate(log.createdAt) }}</td>
                                <td>
                                    <v-chip :color="severityColor(log.severity)" size="x-small">{{ log.severity }}</v-chip>
                                </td>
                                <td class="text-caption">{{ log.event }}</td>
                                <td class="text-caption">{{ log.message }}</td>
                            </tr>
                        </tbody>
                    </v-table>
                    <div v-else class="text-caption text-grey pa-4">Sin eventos registrados.</div>
                </div>
            </div>
        </v-card>

        <SnackbarComponent :value="snackbarData" />
    </div>
</template>

<script>
    import { DatabaseService } from '@/services/DatabaseService';
    import { statusInfo, isTerminal } from './importStatus';
    import moment from 'moment';

    export default {
        name: 'ImportDetailView',
        data () {
            return {
                importData: null,
                logs: [],
                pollTimer: null,
                snackbarData: {
                    openSnackbar: false,
                    description: '',
                },
            };
        },
        computed: {
            importId () {
                return this.$route.params.id;
            },
        },
        created () {
            this.load();
            this.startPolling();
        },
        beforeUnmount () {
            this.stopPolling();
        },
        methods: {
            load () {
                Promise.all([
                    DatabaseService.getImport(this.importId),
                    DatabaseService.getImportLogs(this.importId),
                ]).then(([importResp, logsResp]) => {
                    if (!importResp.error) {
                        this.importData = importResp.response;
                    }

                    if (!logsResp.error) {
                        this.logs = logsResp.response || [];
                    }

                    if (this.importData && isTerminal(this.importData.status)) {
                        this.stopPolling();
                    }
                }).catch(() => {
                    this.stopPolling();
                });
            },
            startPolling () {
                this.stopPolling();
                this.pollTimer = setInterval(() => {
                    if (this.importData && isTerminal(this.importData.status)) {
                        this.stopPolling();
                        return;
                    }
                    this.load();
                }, 3000);
            },
            stopPolling () {
                if (this.pollTimer) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                }
            },
            statusInfo (status) {
                return statusInfo(status);
            },
            severityColor (severity) {
                const colors = {
                    info: 'blue-grey',
                    warning: 'orange',
                    error: 'red',
                };
                return colors[severity] || 'grey';
            },
            formatDate (value) {
                return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '-';
            },
        },
    };
</script>

<style scoped>
    .content_main {
        padding: 16px 20px;
        height: calc(100% - 49px);
        overflow: auto;
    }

    .logs_header {
        margin: 16px 0 8px;
    }

    .logs_container {
        max-height: 360px;
        overflow: auto;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
    }
</style>
